import React, { useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PrinterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { dealerService, DealerInvoice } from '../api/services/dealerService';

const { Title, Text } = Typography;

const statusColor = (s: string) => {
  if (s === 'PAID') return 'success';
  if (s === 'CANCELLED') return 'error';
  return 'warning';
};

const DealerInvoices: React.FC = () => {
  const { t } = useTranslation();
  const userData = JSON.parse(localStorage.getItem('userData') || 'null');
  const dealerId: string | undefined = userData?.dealerId;

  const [invoices, setInvoices] = useState<DealerInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<DealerInvoice | null>(null);

  useEffect(() => {
    if (!dealerId) { setLoading(false); return; }
    dealerService.getDealerInvoices(dealerId)
      .then(setInvoices)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dealerId]);

  const columns: ColumnsType<DealerInvoice> = [
    {
      title: t('dealer_invoices.col_invoice'),
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: t('dealer_invoices.col_date'),
      dataIndex: 'issuedAt',
      key: 'issuedAt',
      render: (v: string) => new Date(v).toLocaleDateString('en-GB'),
    },
    {
      title: t('dealer_invoices.col_description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('dealer_invoices.col_amount'),
      key: 'amount',
      render: (_: any, r: DealerInvoice) => (
        <Text strong style={{ color: '#fe5a1d' }}>
          {Number(r.amount).toLocaleString()} {r.currency}
        </Text>
      ),
    },
    {
      title: t('dealer_invoices.col_status'),
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => <Tag color={statusColor(v)}>{v}</Tag>,
    },
    {
      title: '',
      key: 'action',
      render: (_: any, r: DealerInvoice) => (
        <Button type="link" style={{ color: '#fe5a1d' }} onClick={() => setSelected(r)}>
          {t('dealer_invoices.btn_view')}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 8px 40px' }}>
      <Title level={3} style={{ marginBottom: 24 }}>{t('dealer_invoices.title')}</Title>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={invoices}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 20 }}
            onRow={(r) => ({ onClick: () => setSelected(r), style: { cursor: 'pointer' } })}
            locale={{ emptyText: t('dealer_invoices.empty_text') }}
          />
        )}

        <Drawer
          title={selected ? t('dealer_invoices.drawer_title', { number: selected.invoiceNumber }) : ''}
          open={!!selected}
          onClose={() => setSelected(null)}
          width={440}
          extra={
            <Button icon={<PrinterOutlined />} onClick={() => window.print()}>{t('dealer_invoices.btn_print')}</Button>
          }
        >
          {selected && (
            <div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                {[
                  [t('dealer_invoices.label_invoice_number'), selected.invoiceNumber],
                  [t('dealer_invoices.col_description'), selected.description],
                  [t('dealer_invoices.label_package'), selected.dealerSubscription?.dealerPackage?.name ?? '—'],
                  [t('dealer_invoices.col_amount'), `${Number(selected.amount).toLocaleString()} ${selected.currency}`],
                  [t('dealer_invoices.col_status'), <Tag key="status" color={statusColor(selected.status)}>{selected.status}</Tag>],
                  [t('dealer_invoices.label_issued'), new Date(selected.issuedAt).toLocaleString('en-GB')],
                  ...(selected.paidAt ? [[t('dealer_invoices.label_paid'), new Date(selected.paidAt).toLocaleString('en-GB')]] : []),
                ].map(([label, value], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 0', color: '#888', fontSize: 13, width: '40%' }}>{label}</td>
                    <td style={{ padding: '10px 0', fontWeight: 600, fontSize: 13 }}>{value as any}</td>
                  </tr>
                ))}
              </table>
              {selected.items && (
                <div style={{ marginTop: 20 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{t('dealer_invoices.label_line_items')}</Text>
                  <pre style={{ background: '#f5f5f5', borderRadius: 8, padding: 12, fontSize: 11, marginTop: 6, overflow: 'auto' }}>
                    {JSON.stringify(selected.items, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </Drawer>
    </div>
  );
};

export default DealerInvoices;
